/**
 * Home page — the mock-up's full sequence:
 *   hero (+ circular-economy diagram) → ticker → Think Tank video →
 *   service navigator → infrastructure proof → industries strip →
 *   expert materials + newsletter → quick-contact CTA.
 */
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { MediaAsset } from "../data/types.js";
import { applyPageSeo } from "../core/seo.js";
import { contentSeed } from "../data/seed.js";
import { circularDiagram } from "../components/circular.js";
import { serviceNavigator } from "../components/navigator.js";
import { videoBlock } from "../components/video.js";
import { callbackForm } from "../components/forms.js";
import { newsletterStrip } from "../components/overlays.js";
import { caseCard, industryCard, insightCard, magnetCard } from "../components/cards.js";
import { eyebrow, sectionHead, statBlock } from "../components/common.js";

export async function homeView(ctx: AppContext): Promise<HTMLElement> {
	const copy = ctx.copy();
	const seed = contentSeed(ctx.locale());

	const [categories, articles, caseStudies, site] = await Promise.all([
		ctx.gateway.serviceCategories(),
		ctx.gateway.articles(),
		ctx.gateway.caseStudies(),
		ctx.gateway.siteSettings(),
	]);

	applyPageSeo(ctx, {
		title: `${copy.brand.name} — ${copy.home.heroTitle}`,
		description: copy.home.heroLead,
	});

	const view = el("div", { class: "mb-view mb-view--home" });
	view.appendChild(hero(ctx, site?.heroImage ?? null));
	view.appendChild(ticker(ctx));
	view.appendChild(videoSection(ctx));
	view.appendChild(navigatorSection(ctx, categories));
	view.appendChild(proofSection(ctx));
	view.appendChild(industriesStrip(ctx, seed.industries));
	view.appendChild(materialsSection(ctx, seed, articles));
	view.appendChild(resultsSection(ctx, caseStudies));
	view.appendChild(ctaSection(ctx));
	return view;
}

/**
 * The hero.
 *
 * `heroImage` comes from the CMS and is optional — when an editor has not chosen
 * one, the authored circular-economy diagram is drawn instead. The fallback is the
 * default rather than an error path: the diagram needs no licence, follows the
 * light/dark theme and stays sharp at any size, which is why it was drawn in the
 * first place (see components/circular.ts).
 */
function hero(ctx: AppContext, heroImage: MediaAsset | null): HTMLElement {
	const copy = ctx.copy();

	const searchTrigger = el(
		"button",
		{
			class: "mb-hero__search",
			type: "button",
			onClick: () => ctx.openAssistant(),
		},
		el("span", { class: "mb-hero__searchglass", "aria-hidden": "true" }),
		el("span", { class: "mb-hero__searchtext" }, copy.home.heroSearchPlaceholder),
	);

	const stats = el(
		"div",
		{ class: "mb-hero__stats" },
		...copy.home.heroStats.map((stat) => statBlock(stat.value, stat.label, "plain")),
	);

	const left = el(
		"div",
		{ class: "mb-hero__copy" },
		eyebrow(copy.home.heroEyebrow),
		el("p", { class: "mb-hero__quote" }, copy.brand.quote),
		el("h1", { class: "mb-hero__title" }, copy.home.heroTitle),
		el("p", { class: "mb-hero__lead" }, copy.home.heroLead),
		searchTrigger,
		el(
			"div",
			{ class: "mb-hero__ctas" },
			el("a", { class: "mb-cta mb-cta--accent", href: "#nawigator" }, copy.home.heroCtaPrimary),
			el(
				"a",
				{
					class: "mb-cta mb-cta--outline",
					href: ctx.href("services"),
					"data-routekey": "services",
				},
				copy.home.heroCtaSecondary,
			),
		),
	);

	// The alt text is the editor's when they supplied one; otherwise the hero lead,
	// which describes the same idea the image is there to carry.
	const visual = heroImage
		? el("img", {
				class: "mb-hero__image",
				src: heroImage.url,
				alt: heroImage.alt ?? copy.home.heroLead,
				loading: "eager",
				decoding: "async",
				...(heroImage.width ? { width: String(heroImage.width) } : {}),
				...(heroImage.height ? { height: String(heroImage.height) } : {}),
			})
		: circularDiagram(ctx);

	const right = el("div", { class: "mb-hero__visual" }, visual, stats);

	return el(
		"section",
		{ class: "mb-section mb-section--glow mb-hero" },
		el("div", { class: "mb-section__inner mb-hero__grid" }, left, right),
	);
}

function ticker(ctx: AppContext): HTMLElement {
	const labels = ctx.copy().home.ticker;
	// The strip is duplicated so the marquee can loop seamlessly; the copy is
	// aria-hidden and the whole strip is decorative.
	const items = [...labels, ...labels].map((label) =>
		el("span", { class: "mb-ticker__item" }, label, el("span", { class: "mb-ticker__dot" }, "·")),
	);
	return el(
		"div",
		{ class: "mb-ticker", "aria-hidden": "true" },
		el("div", { class: "mb-ticker__track" }, ...items),
	);
}

function videoSection(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();
	const video = videoBlock(ctx, {
		source: "think-tank",
		poster: "team-stoisko",
		id: "mb-video-thinktank",
		label: copy.home.videoTitle,
	});

	const stats = el(
		"div",
		{ class: "mb-videosection__stats" },
		...copy.home.videoStats.map((stat) => statBlock(stat.value, stat.label)),
	);

	const text = el(
		"div",
		{ class: "mb-videosection__text" },
		eyebrow(copy.home.videoEyebrow),
		el("h2", { class: "mb-videosection__title" }, copy.home.videoTitle),
		el("p", { class: "mb-videosection__lead" }, copy.home.videoLead),
		stats,
		el(
			"div",
			{ class: "mb-videosection__actions" },
			video.controls,
			el(
				"a",
				{ class: "mb-inlinelink", href: ctx.href("knowledge"), "data-routekey": "knowledge" },
				copy.home.videoMore,
			),
		),
	);

	return el(
		"section",
		{ class: "mb-section mb-section--white mb-videosection" },
		el("div", { class: "mb-section__inner mb-videosection__grid" }, text, video.frame),
	);
}

function navigatorSection(
	ctx: AppContext,
	categories: Awaited<ReturnType<AppContext["gateway"]["serviceCategories"]>>,
): HTMLElement {
	return el(
		"section",
		{ class: "mb-section mb-section--glow mb-section--tall" },
		el("div", { class: "mb-section__inner" }, serviceNavigator(ctx, categories)),
	);
}

function proofSection(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();

	const facts = el(
		"div",
		{ class: "mb-proof__facts" },
		...copy.home.proofFacts.map((fact) => statBlock(fact.value, fact.label)),
	);

	const text = el(
		"div",
		{ class: "mb-proof__text" },
		eyebrow(copy.home.proofEyebrow),
		el("h2", { class: "mb-proof__title" }, copy.home.proofTitle),
		el("p", { class: "mb-proof__lead" }, copy.home.proofLead),
		facts,
	);

	const gallery = el(
		"div",
		{ class: "mb-proof__gallery" },
		el("img", {
			class: "mb-proof__wide",
			src: "/static/rozmowy.webp",
			alt:
				ctx.locale() === "en"
					? "Business conversations at a trade fair"
					: "Rozmowy handlowe na targach",
			loading: "lazy",
			decoding: "async",
		}),
		el("img", {
			src: "/static/p5.webp",
			alt: ctx.locale() === "en" ? "The MB Group team" : "Zespół MB Group",
			loading: "lazy",
			decoding: "async",
		}),
		el("img", {
			src: "/static/p7.webp",
			alt: ctx.locale() === "en" ? "The MB Group stand" : "Stoisko MB Group",
			loading: "lazy",
			decoding: "async",
		}),
	);

	return el(
		"section",
		{ class: "mb-section mb-section--white mb-proof mb-section--tall" },
		el("div", { class: "mb-section__inner mb-proof__grid" }, text, gallery),
	);
}

function industriesStrip(
	ctx: AppContext,
	industries: ReturnType<typeof contentSeed>["industries"],
): HTMLElement {
	const copy = ctx.copy();
	const grid = el("div", { class: "mb-grid mb-grid--4" });
	for (const industry of industries) grid.appendChild(industryCard(industry, "strip"));

	return el(
		"section",
		{ class: "mb-section mb-section--paper" },
		el(
			"div",
			{ class: "mb-section__inner" },
			sectionHead(ctx, {
				title: copy.home.industriesTitle,
				link: { label: copy.home.industriesAll, key: "industries" },
				className: "mb-sectionhead--row",
			}),
			grid,
		),
	);
}

function materialsSection(
	ctx: AppContext,
	seed: ReturnType<typeof contentSeed>,
	articles: Awaited<ReturnType<AppContext["gateway"]["articles"]>>,
): HTMLElement {
	const copy = ctx.copy();

	const magnets = el("div", { class: "mb-grid mb-grid--3" });
	seed.magnets.forEach((magnet, index) => magnets.appendChild(magnetCard(ctx, magnet, index)));

	const latest = articles.edges.slice(0, 3).map((edge) => insightCard(ctx, edge.node));
	const insights =
		latest.length > 0 ? el("div", { class: "mb-grid mb-grid--3 mb-grid--tight" }, ...latest) : null;

	return el(
		"section",
		{ class: "mb-section mb-section--white" },
		el(
			"div",
			{ class: "mb-section__inner" },
			sectionHead(ctx, {
				eyebrow: copy.home.magnetsEyebrow,
				title: copy.home.magnetsTitle,
				lead: copy.home.magnetsLead,
				link: { label: copy.home.magnetsAll, key: "knowledge" },
				className: "mb-sectionhead--row",
			}),
			magnets,
			insights,
			newsletterStrip(ctx),
		),
	);
}

function resultsSection(
	ctx: AppContext,
	caseStudies: Awaited<ReturnType<AppContext["gateway"]["caseStudies"]>>,
): HTMLElement | Text {
	if (caseStudies.length === 0) return document.createTextNode("");
	const copy = ctx.copy();
	const grid = el("div", { class: "mb-grid mb-grid--3" });
	for (const study of caseStudies.slice(0, 3)) grid.appendChild(caseCard(ctx, study));

	return el(
		"section",
		{ class: "mb-section mb-section--paper" },
		el(
			"div",
			{ class: "mb-section__inner" },
			sectionHead(ctx, {
				title: copy.cases.title,
				lead: copy.cases.lead,
				link: { label: copy.serviceDetail.relatedAll, key: "caseStudies" },
				className: "mb-sectionhead--row",
			}),
			grid,
		),
	);
}

function ctaSection(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();

	const facts = el(
		"div",
		{ class: "mb-ctasection__facts" },
		...copy.home.ctaFacts.map((fact) => statBlock(fact.value, fact.label)),
	);

	const text = el(
		"div",
		{ class: "mb-ctasection__text" },
		eyebrow(copy.home.ctaEyebrow),
		el("h2", { class: "mb-ctasection__title" }, copy.home.ctaTitle),
		el("p", { class: "mb-ctasection__lead" }, copy.home.ctaLead),
		facts,
	);

	return el(
		"section",
		{ class: "mb-section mb-section--glow" },
		el("div", { class: "mb-section__inner mb-ctasection__grid" }, text, callbackForm(ctx)),
	);
}
